'use client';

import React, { useState, useCallback, useMemo } from 'react';
import BigNumber from 'bignumber.js';
import { useSearchParams } from 'next/navigation';
import { HiDocumentText } from 'react-icons/hi';
import { MdOutlineSearchOff } from 'react-icons/md';
import { FaFileContract } from 'react-icons/fa';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';
import styles from './TransactionsPage.module.css';
import useQueryWithPages from 'src/hooks/useQueryWithPages';
import { useQuery } from '@tanstack/react-query';
import useNewTxsSocket from 'lib/hooks/useNewTxsSocket';
import { formatTimestamp } from '../../utils/formatTimestamp';
import FilterDropdown from 'src/app/components/FilterDropdown';
import type { FilterSection } from 'src/app/components/FilterDropdown';

const TX_TYPE_OPTIONS: FilterSection = {
  title: 'Transaction Type',
  type: 'checkbox',
  key: 'type',
  options: [
    { value: 'coin_transfer', label: 'Coin Transfer' },
    { value: 'token_transfer', label: 'Token Transfer' },
    { value: 'contract_call', label: 'Contract Call' },
    { value: 'contract_creation', label: 'Contract Creation' },
    { value: 'token_creation', label: 'Token Creation' },
  ],
};

const TX_METHOD_OPTIONS: FilterSection = {
  title: 'Method',
  type: 'checkbox',
  key: 'method',
  options: [
    { value: 'approve', label: 'Approve' },
    { value: 'transfer', label: 'Transfer' },
    { value: 'multicall', label: 'Multicall' },
    { value: 'mint', label: 'Mint' },
    { value: 'commit', label: 'Commit' },
  ],
};

const TransactionsPage: React.FC = () => {
  const searchParams = useSearchParams();
  const tab = searchParams?.get('tab') || 'validated';
  const [filterValues, setFilterValues] = useState<Record<string, Array<string>>>({ type: [], method: [] });

  const activeFilters = useMemo(() => {
    const filters: Record<string, any> = { filter: 'validated' as const };
    if (filterValues.type?.length) filters.type = filterValues.type;
    if (filterValues.method?.length) filters.method = filterValues.method;
    return filters;
  }, [filterValues]);

  const pendingFilters = useMemo(() => {
    const filters: Record<string, any> = { filter: 'pending' as const };
    if (filterValues.type?.length) filters.type = filterValues.type;
    if (filterValues.method?.length) filters.method = filterValues.method;
    return filters;
  }, [filterValues]);

  const txsValidatedQuery = useQueryWithPages({
    resourceName: 'txs_validated',
    filters: activeFilters,
    sorting: { sort: 'block_number', order: 'desc' },
    options: {
      enabled: tab === 'validated',
    },
  });

  const txsPendingQuery = useQueryWithPages({
    resourceName: 'txs_pending',
    filters: pendingFilters,
    options: {
      enabled: tab === 'pending',
    },
  });

  const { num } = useNewTxsSocket();

  const statsQuery = useQuery({
    queryKey: ['stats_transactions'],
    queryFn: async() => {
      const res = await fetch(`${process.env.NEXT_PUBLIC_STATS_API_HOST}/api/v1/pages/transactions`);
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json();
    },
    refetchInterval: 30000,
  });
  const stats = statsQuery.data as any;

  const formatNumber = (value: string | undefined) => {
    if (!value) return '—';
    return Number(value).toLocaleString();
  };

  const formatFee = (value: string | undefined) => {
    if (!value) return '—';
    const num = parseFloat(value);
    return num < 0.01 ? num.toFixed(6) : num.toFixed(2);
  };

  const formatWei = (value: string | null | undefined) => {
    if (!value || value === '0') return '0';
    const bn = BigNumber(value);
    const eth = bn.dividedBy(BigNumber(10).pow(18));
    if (eth.isGreaterThanOrEqualTo(1000000)) {
      return `${eth.dividedBy(1000000).toFixed(2)}M`;
    }
    if (eth.isGreaterThanOrEqualTo(1000)) {
      return `${eth.dividedBy(1000).toFixed(2)}K`;
    }
    if (eth.isLessThan(0.000001) && !eth.isZero()) {
      return eth.toExponential(2);
    }
    return eth.toFixed(6);
  };

  const handleFilterApply = useCallback((values: Record<string, Array<string>>) => {
    setFilterValues(values);
  }, []);

  const handleFilterReset = useCallback(() => {
    setFilterValues({ type: [], method: [] });
  }, []);

  const currentQuery = tab === 'pending' ? txsPendingQuery : txsValidatedQuery;
  const transactions = currentQuery.data?.items || [];

  return (
    <div className={styles.container}>
      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Transactions</div>
          <div className={styles.statValue}>
            {formatNumber(stats?.transactions_24h?.value)} <span className={styles.statTime}>(24h)</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Pending transactions</div>
          <div className={styles.statValue}>
            {formatNumber(stats?.pending_transactions_30m?.value)} <span className={styles.statTime}>(30min)</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Transactions fees</div>
          <div className={styles.statValue}>
            {formatFee(stats?.transactions_fee_24h?.value)} OGPU <span className={styles.statTime}>(24h)</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Avg. transaction fee</div>
          <div className={styles.statValue}>
            {formatFee(stats?.average_transactions_fee_24h?.value)} OGPU <span className={styles.statTime}>(24h)</span>
          </div>
        </div>
      </div>

      {/* Tabs and Controls */}
      <div className={styles.controls}>
        <div className={styles.tabs}>
          <a
            href="?tab=validated"
            className={`${styles.tab} ${tab === 'validated' ? styles.tabActive : ''}`}
          >
            Mined
          </a>
          <a
            href="?tab=pending"
            className={`${styles.tab} ${tab === 'pending' ? styles.tabActive : ''}`}
          >
            Pending
          </a>
        </div>
        <div className={styles.rightControls}>
          <FilterDropdown
            label="Advanced filter"
            sections={[TX_TYPE_OPTIONS, TX_METHOD_OPTIONS]}
            values={filterValues}
            onApply={handleFilterApply}
            onReset={handleFilterReset}
          />
          <div className={styles.pagination}>
            <button
              className={styles.pageButton}
              disabled={!currentQuery.pagination.canGoBackwards}
              onClick={currentQuery.pagination.onPrevPageClick}
            >
              First
            </button>
            <button
              className={styles.pageButton}
              disabled={!currentQuery.pagination.canGoBackwards}
              onClick={currentQuery.pagination.onPrevPageClick}
            >
              &lt;
            </button>
            <button className={`${styles.pageButton} ${styles.pageButtonActive}`}>
              {currentQuery.pagination.page}
            </button>
            <button
              className={styles.pageButton}
              disabled={!currentQuery.pagination.hasNextPage}
              onClick={currentQuery.pagination.onNextPageClick}
            >
              &gt;
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Txn hash</th>
              <th>Type</th>
              <th>Method</th>
              <th>Block</th>
              <th>From/To</th>
              <th>Value OGPU</th>
              <th>Fee OGPU</th>
            </tr>
          </thead>
          <tbody>
            {num > 0 && !currentQuery.isLoading && (
              <tr className={styles.scanningRow}>
                <td colSpan={7}>
                  <div className={styles.scanning}>
                    scanning new transactions...
                  </div>
                </td>
              </tr>
            )}
            {currentQuery.isLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={i}>
                  <td><div className={styles.skeleton} style={{ width: '7rem', height: '0.875rem' }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '5rem', height: '0.875rem' }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '4rem', height: '0.875rem' }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '3.5rem', height: '0.875rem' }} /></td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', minWidth: 0 }}>
                        <div className={styles.skeleton} style={{ width: '1rem', height: '1rem', borderRadius: '50%', flexShrink: 0 }} />
                        <div className={styles.skeleton} style={{ width: '6rem', height: '0.875rem' }} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', minWidth: 0 }}>
                        <div className={styles.skeleton} style={{ width: '1rem', height: '1rem', borderRadius: '50%', flexShrink: 0 }} />
                        <div className={styles.skeleton} style={{ width: '6rem', height: '0.875rem' }} />
                      </div>
                    </div>
                  </td>
                  <td><div className={styles.skeleton} style={{ width: '4rem', height: '0.875rem' }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '4rem', height: '0.875rem' }} /></td>
                </tr>
              ))
            ) : transactions.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className={styles.emptyState}>
                    <MdOutlineSearchOff size={48} />
                    <p>There are no transactions.</p>
                  </div>
                </td>
              </tr>
            ) : (
              transactions.map((tx: any, index: number) => (
                <tr key={tx.hash || index}>
                  <td>
                    <a href={`/tx/${tx.hash}`} className={styles.link}>
                      {tx.hash?.slice(0, 10)}...{tx.hash?.slice(-4)}
                    </a>
                    <div className={styles.age}>{formatTimestamp(tx.timestamp)}</div>
                  </td>
                  <td>
                    <span className={styles.badge}>
                      {tx.transaction_types?.includes('contract_call') ? 'Contract call' : 'Transfer'}
                    </span>
                    {tx.status === 'ok' && (
                      <span className={styles.statusSuccess}>✓ Success</span>
                    )}
                  </td>
                  <td>
                    <span className={styles.method}>{tx.method || 'Transfer'}</span>
                  </td>
                  <td>
                    <a href={`/block/${tx.block_number}`} className={styles.link}>
                      {tx.block_number}
                    </a>
                  </td>
                  <td>
                    <div className={styles.addressGroup}>
                      <div className={styles.addressRow}>
                        <span className={styles.arrow}>↓</span>
                        { tx.from?.hash && (
                          <span style={{ flexShrink: 0, display: 'inline-flex' }}>
                            <Jazzicon diameter={16} seed={jsNumberForAddress(tx.from.hash)} />
                          </span>
                        ) }
                        <a href={`/address/${tx.from?.hash}`} className={styles.address}>
                          {tx.from?.hash?.slice(0, 6)}...{tx.from?.hash?.slice(-4)}
                        </a>
                      </div>
                      <div className={styles.addressRow}>
                        { tx.to?.is_contract ? (
                          <FaFileContract size={14} className={styles.icon} color="#3b82f6" />
                        ) : tx.to?.hash ? (
                          <span style={{ flexShrink: 0, display: 'inline-flex' }}>
                            <Jazzicon diameter={16} seed={jsNumberForAddress(tx.to.hash)} />
                          </span>
                        ) : (
                          <HiDocumentText size={14} className={styles.icon} />
                        ) }
                        <a href={`/address/${tx.to?.hash}`} className={styles.address}>
                          {tx.to?.hash?.slice(0, 6)}...{tx.to?.hash?.slice(-4)}
                        </a>
                      </div>
                    </div>
                  </td>
                  <td className={styles.value}>{formatWei(tx.value)}</td>
                  <td className={styles.fee}>{formatWei(tx.fee?.value)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TransactionsPage;
